/**
 * SummaryCalculator.ts
 * Handles calculation of summary statistics (high, low, mean, median) for financial data
 *
 * This service provides statistical analysis for financial datasets, supporting:
 * - Multiple data formats (currency, percentage, multiples, raw numbers)
 * - Automatic format detection and preservation
 * - Robust parsing with special value handling (NM, N/A, -)
 * - DOM-based table summary updates
 */

import { formatCurrency, formatPercentage, formatMultiple } from '../../utils/formatters';

/**
 * Format type for determining how to format calculated values
 */
type FormatType = 'currency' | 'percentage' | 'multiple' | 'number';

/**
 * Service for calculating summary statistics from company data
 *
 * Uses standard statistical formulas:
 * - High: max(values)
 * - Low: min(values)
 * - Mean: sum(values) / count(values)
 * - Median: middle value when sorted (or average of two middle values for even count)
 */
export class SummaryCalculator {
  /**
   * Calculate summary statistics (high, low, mean, median) for a dataset
   *
   * Processes each metric independently, parsing values, calculating statistics,
   * and formatting results to match the original data format.
   *
   * @template T - Type extending Record<string, any> representing company data structure
   * @param data - Array of company data objects
   * @param metrics - Array of property keys to calculate statistics for
   * @returns Summary object with high, low, mean, and median for each metric
   *
   * @example
   * ```ts
   * const companies = [
   *   { name: 'NVDA', revenue: '$26,974', margin: '15.6%' },
   *   { name: 'AMD', revenue: '$22,680', margin: '12.3%' }
   * ];
   * const summary = calculator.calculateSummary(companies, ['revenue', 'margin']);
   * // Returns: {
   * //   high: { revenue: '$26,974', margin: '15.6%' },
   * //   low: { revenue: '$22,680', margin: '12.3%' },
   * //   mean: { revenue: '$24,827', margin: '14.0%' },
   * //   median: { revenue: '$24,827', margin: '14.0%' }
   * // }
   * ```
   */
  public calculateSummary<T extends Record<string, unknown>>(
    data: T[],
    metrics: (keyof T)[]
  ): {
    high: Partial<T>;
    low: Partial<T>;
    mean: Partial<T>;
    median: Partial<T>;
  } {
    const summary = {
      high: {} as Partial<T>,
      low: {} as Partial<T>,
      mean: {} as Partial<T>,
      median: {} as Partial<T>
    };

    metrics.forEach(metric => {
      // Extract and parse values for this metric
      const values = data
        .map(company => this.parseValue(String(company[metric])))
        .filter((val): val is number => val !== null);

      if (values.length === 0) {
        return; // Skip metrics with no valid values
      }

      // Calculate statistics
      const high = this.calculateHigh(values);
      const low = this.calculateLow(values);
      const mean = this.calculateMean(values);
      const median = this.calculateMedian(values);

      // Determine format type from original value
      const firstItem = data[0];
      if (!firstItem) return; // Skip if no data
      const formatType = this.determineFormatType(String(firstItem[metric]));

      // Format and store results
      summary.high[metric] = this.formatValue(high, formatType) as T[keyof T];
      summary.low[metric] = this.formatValue(low, formatType) as T[keyof T];
      summary.mean[metric] = this.formatValue(mean, formatType) as T[keyof T];
      summary.median[metric] = this.formatValue(median, formatType) as T[keyof T];
    });

    return summary;
  }

  /**
   * Recalculate summary statistics for a dataset after modifications
   *
   * Automatically detects all numeric metrics (excluding 'name' field) and
   * updates the dataset.summary property with new calculations.
   *
   * @template T - Type extending Record<string, any> representing company data structure
   * @param dataset - Dataset object containing companies array and summary property
   *
   * @example
   * ```ts
   * const dataset = {
   *   companies: [
   *     { name: 'NVDA', price: '120.50', shares: '2456' },
   *     { name: 'AMD', price: '95.75', shares: '1623' }
   *   ],
   *   summary: {} // Will be populated
   * };
   * calculator.updateDatasetSummary(dataset);
   * // dataset.summary now contains high/low/mean/median for price and shares
   * ```
   */
  public updateDatasetSummary<T extends Record<string, unknown>>(dataset: {
    companies: T[];
    summary?: Record<string, Record<string, string | number>>;
  }): void {
    if (dataset.companies.length === 0) {
      dataset.summary = {
        high: {},
        low: {},
        mean: {},
        median: {}
      };
      return;
    }

    // Get all metrics (exclude 'name' field)
    const firstCompany = dataset.companies[0];
    if (!firstCompany) return; // No companies to calculate metrics from
    const metrics = Object.keys(firstCompany).filter(key => key !== 'name') as (keyof T)[];

    // Calculate new summary
    const newSummary = this.calculateSummary(dataset.companies, metrics);

    // Update dataset
    dataset.summary = {
      high: newSummary.high as Record<string, string | number>,
      low: newSummary.low as Record<string, string | number>,
      mean: newSummary.mean as Record<string, string | number>,
      median: newSummary.median as Record<string, string | number>
    };
  }

  /**
   * Calculate high (maximum) value
   *
   * @param values - Array of numeric values
   * @returns Maximum value in the array
   */
  private calculateHigh(values: number[]): number {
    return Math.max(...values);
  }

  /**
   * Calculate low (minimum) value
   *
   * @param values - Array of numeric values
   * @returns Minimum value in the array
   */
  private calculateLow(values: number[]): number {
    return Math.min(...values);
  }

  /**
   * Calculate mean (average) value
   *
   * Formula: mean = sum(values) / count(values)
   *
   * @param values - Array of numeric values
   * @returns Arithmetic mean of values, or 0 if array is empty
   */
  private calculateMean(values: number[]): number {
    if (values.length === 0) return 0;
    const sum = values.reduce((a, b) => a + b, 0);
    return sum / values.length;
  }

  /**
   * Calculate median value
   *
   * Algorithm:
   * 1. Sort values in ascending order
   * 2. If odd count: return middle value
   * 3. If even count: return average of two middle values
   *
   * @param values - Array of numeric values
   * @returns Median value, or 0 if array is empty
   */
  private calculateMedian(values: number[]): number {
    if (values.length === 0) return 0;

    // Sort values (create copy to avoid mutating original)
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);

    // If odd length, return middle value
    if (sorted.length % 2 !== 0) {
      return sorted[middle] ?? 0;
    }

    // If even length, return average of two middle values
    const left = sorted[middle - 1] ?? 0;
    const right = sorted[middle] ?? 0;
    return (left + right) / 2;
  }

  /**
   * Parse a string value into a number
   *
   * Handles various financial formats:
   * - Currency: "$1,234.56" → 1234.56
   * - Percentage: "15.6%" → 15.6
   * - Multiples: "5.5x" → 5.5
   * - Special values: "NM", "N/A", "-" → null
   *
   * @param value - String value to parse
   * @returns Parsed numeric value, or null if unparseable or special value
   *
   * @example
   * ```ts
   * parseValue("$1,234.56");  // → 1234.56
   * parseValue("15.6%");      // → 15.6
   * parseValue("NM");         // → null
   * parseValue("-");          // → null
   * ```
   */
  private parseValue(value: string): number | null {
    if (!value || value === '-' || value === 'NM' || value === 'N/A') {
      return null;
    }

    // Remove all non-numeric characters except decimal point and minus sign
    const cleaned = value.replace(/[^-\d.]/g, '');

    const parsed = parseFloat(cleaned);

    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Determine the format type based on the original value
   *
   * Detection rules (in priority order):
   * 1. Contains '%' → percentage
   * 2. Contains 'x' → multiple
   * 3. Contains '$' or ',' → currency
   * 4. Otherwise → number
   *
   * @param value - Original string value to analyze
   * @returns Format type identifier
   */
  private determineFormatType(value: string): FormatType {
    if (value.includes('%')) {
      return 'percentage';
    }
    if (value.includes('x')) {
      return 'multiple';
    }
    if (value.includes('$') || value.includes(',')) {
      return 'currency';
    }
    return 'number';
  }

  /**
   * Format a numeric value based on the specified format type
   *
   * Applies appropriate formatting while preserving original data semantics:
   * - currency: "$1,234.56" (2 decimals)
   * - percentage: "15.6%" (1 decimal, value already in percent form)
   * - multiple: "5.5x" (1 decimal)
   * - number: "123.45" (2 decimals)
   *
   * @param value - Numeric value to format
   * @param formatType - Type of formatting to apply
   * @returns Formatted string representation
   */
  private formatValue(value: number, formatType: FormatType): string {
    switch (formatType) {
      case 'currency':
        return formatCurrency(value, 2);
      case 'percentage':
        // Values are already in percent form (e.g., 15.6 not 0.156), so use isAlreadyPercent=true
        return formatPercentage(value, 1, true);
      case 'multiple':
        return formatMultiple(value, 1);
      case 'number':
        return value.toFixed(2);
      default:
        return value.toFixed(2);
    }
  }

  /**
   * Update summary statistics for a table based on current visible rows
   *
   * DOM-based calculation that:
   * 1. Extracts values from each visible table row
   * 2. Parses values using parseValue()
   * 3. Calculates statistics per column
   * 4. Updates summary table cells with formatted results
   *
   * Useful after company deletion or filtering operations.
   *
   * @param table - Main data table containing company rows
   * @param summaryTable - Summary table to update (should have 4 rows: high, low, mean, median)
   *
   * @example
   * ```ts
   * const dataTable = document.querySelector('.financials-table');
   * const summaryTable = document.querySelector('.summary-table');
   * calculator.updateTableSummary(dataTable, summaryTable);
   * // Summary table cells are now updated with recalculated statistics
   * ```
   */
  public updateTableSummary(
    table: HTMLTableElement,
    summaryTable: HTMLTableElement
  ): void {
    const rows = Array.from(table.querySelectorAll('tbody tr'));
    const numColumns = table.querySelectorAll('thead th').length;

    // For each numeric column (skip checkbox and company name)
    for (let col = 3; col <= numColumns; col++) {
      // Extract values from table cells
      const values = rows
        .map(row => {
          const cell = row.querySelector(`td:nth-child(${col})`);

          if (!cell) return null;

          // Skip non-numeric cells (like dates)
          if (cell.classList.contains('date')) return null;

          return this.parseValue(cell.textContent?.trim() || '');
        })
        .filter((val): val is number => val !== null);

      if (values.length === 0) continue;

      // Calculate statistics
      const high = this.calculateHigh(values);
      const low = this.calculateLow(values);
      const mean = this.calculateMean(values);
      const median = this.calculateMedian(values);

      // Determine format from header
      const headerCell = table.querySelector(`thead th:nth-child(${col})`);
      const formatType = this.determineFormatType(headerCell?.textContent || '');

      // Update summary table cells
      const summaryRows = summaryTable.querySelectorAll('tbody tr');

      if (summaryRows.length >= 4) {
        const targetCol = col - 1; // Adjust for checkbox column

        const highCell = summaryRows[0]?.querySelector(`td:nth-child(${targetCol})`);
        const lowCell = summaryRows[1]?.querySelector(`td:nth-child(${targetCol})`);
        const meanCell = summaryRows[2]?.querySelector(`td:nth-child(${targetCol})`);
        const medianCell = summaryRows[3]?.querySelector(`td:nth-child(${targetCol})`);

        if (highCell) highCell.textContent = this.formatValue(high, formatType);
        if (lowCell) lowCell.textContent = this.formatValue(low, formatType);
        if (meanCell) meanCell.textContent = this.formatValue(mean, formatType);
        if (medianCell) medianCell.textContent = this.formatValue(median, formatType);
      }
    }
  }
}
